import { ExclamationCircleSolid, Times } from '@assets/svgs';
import { IconButton } from '@components/iconButton';
import { Box, SnackbarProps, Typography, SnackbarContent } from '@mui/material';
import { SnackbarKey, useSnackbar } from 'notistack';
import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { ToastBaseProps } from './Toast';
import { getOrCreateSessionId } from '@utils/sessionId';

export type ToastProps = SnackbarProps &
  ToastBaseProps & {
    actionFunction?: (id: SnackbarKey) => void;
    noAutoHide?: boolean;
    errorList?: Array<string | React.ReactNode>;
  };

type ErrorListToastItemsProps = {
  errorListItems?: Array<string | React.ReactNode>;
};

const ErrorListToastItems = ({ errorListItems }: ErrorListToastItemsProps) => {
  return errorListItems ? (
    <Typography variant='bodySmall'>
      <ul style={{ margin: '0' }}>
        {errorListItems.map((item) => (
          <li key={`${item}-key`}>{item}</li>
        ))}
      </ul>
    </Typography>
  ) : null;
};

const StyledSnackbar = styled(SnackbarContent, {
  name: 'ErrorListToast',
  slot: 'Root',
})<SnackbarProps>(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.primary.dark,
  borderLeft: `6px solid ${theme.palette.error.main}`,
  justifyContent: 'space-between',
  width: '400px',
  minHeight: '56px',
  flexWrap: 'nowrap',
  '.MuiSnackbarContent-message': {
    color: theme.palette.common.white,
    maxWidth: '350px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  '& .MuiSnackbarContent-action': {
    position: 'absolute',
    top: '0px',
    right: '8px',
    paddingLeft: '10px',
  },
  '& .MuiSnackbarContent-action .MuiButton-root': {
    color: 'white',
    '&:hover': {
      textDecoration: 'none',
      color: 'white',
    },
  },
  '.errorList': {
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
  },

  li: {
    wordBreak: 'break-word',
  },

  // remove overlay which is making the item lighter
  '--Paper-overlay': 'unset !important',
}));

export const ErrorListToast = React.forwardRef<HTMLDivElement, ToastProps>(
  ({ errorList, message, variant, id, showSessionId = false }, ref) => {
    const { closeSnackbar } = useSnackbar();

    const handleDismiss = useCallback(() => {
      closeSnackbar(id);
    }, [id, closeSnackbar]);

    const action = (
      <IconButton
        sx={{
          position: 'absolute',
          top: 2,
          right: 2,
          svg: {
            color: 'text.main',
          },
        }}
        variant='circularDark'
        size='small'
        onClick={handleDismiss}
      >
        <Times width='10px' height='10px' />
      </IconButton>
    );
    const sessionId = showSessionId ? getOrCreateSessionId() : null;

    return (
      <StyledSnackbar
        ref={ref}
        action={action}
        message={
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex' }}>
              <IconButton
                sx={{ margin: '0 8px 0 8px', cursor: 'auto', height: 'auto', color: 'error.main' }}
                className={variant}
                disableRipple
              >
                <ExclamationCircleSolid />
              </IconButton>
              <div style={{ height: '36px', display: 'flex', alignItems: 'center' }}>
                <Typography
                  variant='bodySmall'
                  sx={{
                    fontWeight: 'bold',
                  }}
                >
                  {message}
                </Typography>
              </div>
            </div>
            <ErrorListToastItems errorListItems={errorList} />
            {sessionId && (
              <Typography
                variant='bodySmall'
                sx={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '10px',
                  mt: '4px',
                  pl: '8px',
                  wordBreak: 'break-all',
                }}
              >
                Session: {sessionId}
              </Typography>
            )}
          </Box>
        }
      />
    );
  },
);

ErrorListToast.displayName = 'ErrorListToast';
